import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const sectors = [
  {
    icon: "ri-shield-star-line",
    label: "Defence",
    tagline: "From capability to contract.",
    desc: "Single-service programmes, joint requirements, allied procurement, and an acquisition system where evidence and route selection determine whether capability reaches the front line.",
  },
  {
    icon: "ri-rocket-line",
    label: "Space",
    tagline: "A sovereign priority, an emerging market.",
    desc: "Government, defence, and commercial buyers with different procurement patterns, different compliance thresholds, and a shared need to move faster than the system currently allows.",
  },
  {
    icon: "ri-lock-password-line",
    label: "Cyber",
    tagline: "Proven capability. Crowded market.",
    desc: "Framework listings and pilot projects create visibility but not contracts. Differentiation in this market is about positioning and pursuit strategy, not capability.",
  },
  {
    icon: "ri-building-4-line",
    label: "Critical Infrastructure",
    tagline: "Operators who cannot afford to get it wrong.",
    desc: "Commercial procurement layered with security and resilience requirements that most suppliers underestimate, in organisations that move slowly and carefully by design.",
  },
];

export default function About() {
  const bodyRef = useInView();
  const sectorsRef = useInView();

  useEffect(() => {
    document.title = "About Resecor | UK National Security & Defence Advisory Firm";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "Founded on direct experience of UK national security, defence, and resilience markets. Resecor brings buyer-side insight to supplier-side strategy — helping credible companies navigate procurement faster.");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${import.meta.env.VITE_SITE_URL ?? ""}/about`);

    const scriptId = "jsonld-about";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/about#webpage`,
          "url": `${import.meta.env.VITE_SITE_URL ?? ""}/about`,
          "name": "About Resecor | UK National Security & Defence Advisory Firm",
          "description": "Founded on direct experience of UK national security, defence, and resilience markets. Resecor brings buyer-side insight to supplier-side strategy.",
          "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#website` },
          "about": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#organization` }
        },
        {
          "@type": "ProfessionalService",
          "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#organization`,
          "name": "Resecor",
          "url": `${import.meta.env.VITE_SITE_URL ?? ""}/`,
          "email": "enquiries@resecor.com",
          "description": "Resecor is a specialist advisory firm for technology companies pursuing contracts in UK national security, defence, space, cyber, and critical infrastructure markets.",
          "areaServed": "GB",
          "knowsAbout": ["Defence Procurement", "National Security", "Space Markets", "Cyber Security", "Critical Infrastructure", "UK Government Advisory"]
        }
      ]
    });

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-navy font-body">
      <Navbar />

      {/* ── PAGE HERO ── */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/37ab716c6c5847877b880a1c80bcd0d6.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/80 to-navy" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <span className="font-heading text-xs tracking-[0.25em] uppercase text-copper font-medium">About</span>
          <h1 className="mt-4 font-heading font-bold text-3xl md:text-5xl text-white leading-tight max-w-2xl">
            Built for this moment.
          </h1>
          <div className="mt-6 w-12 h-0.5 bg-copper" />
        </div>
      </section>

      {/* ── BODY COPY ── */}
      <section className="bg-navy pb-20 md:pb-24" ref={bodyRef.ref}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            className={`max-w-3xl flex flex-col gap-6 transition-all duration-700 ${bodyRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <p className="font-body text-base md:text-lg text-mid-grey leading-loose">
              Most technology companies that stall in these markets do not have a technology problem. They have a navigation problem. We work at the point where proven capability meets a system that most suppliers cannot navigate alone.
            </p>
            <p className="font-body text-base md:text-lg text-mid-grey leading-loose">
              <strong className="text-white font-medium">We exist to bring velocity to the system.</strong> From proven capability to contracted outcome, faster.
            </p>
            <p className="font-body text-base md:text-lg text-mid-grey leading-loose">
              Founded on direct experience of the markets we serve, government advisory, and commercial strategy. We have sat where operators define the need, where buyers commit the budget, and where suppliers compete for the contract.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTORS ── */}
      <section className="bg-slate py-20 md:py-28" ref={sectorsRef.ref}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            className={`transition-all duration-700 ${sectorsRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <span className="font-heading text-xs tracking-[0.25em] uppercase text-copper font-medium">Markets</span>
            <h2 className="mt-3 font-heading font-bold text-2xl md:text-4xl text-white leading-tight max-w-xl">
              Where we work.
            </h2>
            <p className="mt-4 font-body text-sm md:text-base text-mid-grey leading-relaxed max-w-2xl">
              Resecor focuses on four markets within UK national security and resilience. Each has distinct buyers, procurement patterns, and compliance requirements. We know how each one works.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 rounded-sm overflow-hidden">
            {sectors.map((s, i) => (
              <div
                key={s.label}
                className={`group relative bg-slate hover:bg-charcoal transition-all duration-500 p-8 md:p-10 flex flex-col gap-5 ${
                  sectorsRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 100 + 200}ms` }}
              >
                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-navy/60 rounded-sm group-hover:bg-copper/10 transition-colors duration-300">
                    <i className={`${s.icon} text-copper text-xl`} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-white group-hover:text-copper transition-colors duration-200">
                      {s.label}
                    </h3>
                    <p className="mt-0.5 font-body text-xs text-copper/70 font-medium tracking-wide">{s.tagline}</p>
                  </div>
                </div>
                <div className="w-8 h-px bg-copper/25 group-hover:w-14 group-hover:bg-copper/60 transition-all duration-300" />
                <p className="font-body text-sm text-mid-grey leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
